import { Navigate, useLocation } from 'react-router';
import { SignUpPage } from '~/pages/authentication/SignUpPage';
import {
  CLERK_SATELLITE_URL_PARAM,
  RETURN_TO_PARAM,
  buildAuthCompletePath,
  readAuthHandoffUrl,
  sanitizeOAuthCompletionUrl,
  sanitizeReturnTo,
  sanitizeSatelliteReturnUrl,
} from './returnTo';
import { paths } from './routes';

/** Preserve Clerk continuation and satellite exchange routes after its session activates. */
export const AuthenticatedSignUpRoute = ({ path = paths.publicAuth.signUp }: { path?: string }) => {
  const location = useLocation();
  const origin = window.location.origin;
  const params = new URLSearchParams(location.search);
  const invitationCode =
    path === paths.publicAuth.signUpEmployer ? params.get('invitationCode') : null;
  const invitationReturnTo = invitationCode
    ? `${paths.employerUserInvitation.accept}?code=${encodeURIComponent(invitationCode)}`
    : null;
  const continuation = <SignUpPage path={path} returnTo={invitationReturnTo} />;
  const nestedPath = location.pathname.startsWith(`${path}/`)
    ? location.pathname.slice(path.length).replace(/\//g, '')
    : '';
  const nestedHash = location.hash.startsWith('#/')
    ? location.hash.slice(2).split('?', 1)[0].replace(/\//g, '')
    : '';

  if (nestedPath || nestedHash) return continuation;

  const handoff = sanitizeOAuthCompletionUrl(
    readAuthHandoffUrl(location.search, location.hash),
    origin
  );
  if (handoff) {
    const url = new URL(handoff, origin);
    // The prebuilt surface must finish the primary-to-satellite exchange.
    if (url.origin !== origin) return continuation;

    const destination = `${url.pathname}${url.search}${url.hash}`;
    return (
      <Navigate
        to={
          url.pathname === paths.publicAuth.authComplete
            ? destination
            : buildAuthCompletePath(sanitizeReturnTo(destination, origin))
        }
        replace
      />
    );
  }

  if (sanitizeSatelliteReturnUrl(params.get(CLERK_SATELLITE_URL_PARAM))) return continuation;

  const returnTo = sanitizeReturnTo(params.get(RETURN_TO_PARAM) ?? invitationReturnTo, origin);
  return <Navigate to={buildAuthCompletePath(returnTo)} replace />;
};
